import { TEAMS_LINK, TeamsLogo, WHATSAPP_NUMBER, WhatsAppLogo } from "@/lib";
import { motion } from "framer-motion";
import { CheckCircle2, TrendingUp, Sparkles } from "lucide-react";

export default function ITPackageSection (){
    
    // CONTENT REVERTED TO ORIGINAL USER DATA, MAPPED TO NEW STRUCTURE
    const plans = [
        {
            name: 'Starter',
            description: 'Ideal for startups and small teams needing foundational IT support.',
            priceLabel: 'On-Demand', // Mapped for design purposes
            icon: TrendingUp,
            features: [
                'On-Demand IT Support',
                'System Setup & Configuration',
                'Basic Security Audits',
                'Technology Consultation',
            ],
            popular: false,
        },
        {
            name: 'Business',
            description: 'The perfect package for growing businesses needing proactive support.',
            priceLabel: 'Retainer', // Mapped for design purposes
            icon: Sparkles,
            features: [
                'Managed IT Services',
                'Monthly Support Credit Hours',
                'Proactive System Monitoring',
                'Ongoing Consultancy',
                'Priority Response',
            ],
            popular: true,
        },
        {
            name: 'Enterprise',
            description: 'Full-scale technology partnership for established enterprises.',
            priceLabel: 'Custom', // Mapped for design purposes
            icon: TrendingUp,
            features: [
                'Fractional CTO / Advisory',
                'Custom Architecture Design',
                'Full Team Augmentation',
                '24/7/365 Support',
                'Strategic R&D',
            ],
            popular: false,
        },
    ];

    const cardVariants = {
        hidden: { opacity: 0, y: 30 },
        visible: { opacity: 1, y: 0, transition: { duration: 0.5 } }
    };

    return (
        <section id="plans" className="py-24 bg-slate-900 relative overflow-hidden">
            {/* Background Gradients */}
            <div className="absolute top-0 right-0 w-full h-full pointer-events-none opacity-10">
                <div className="absolute top-[-10%] left-[-10%] w-[500px] h-[500px] bg-sky-900/50 rounded-full blur-[120px]" />
            </div>

            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
                {/* Header - Using original title */}
                <div className="text-center max-w-3xl mx-auto mb-16">
                    <span className="text-sm font-semibold uppercase text-sky-400 tracking-wider">Consultancy Plans</span>
                    <h2 className="mt-2 text-4xl sm:text-5xl font-extrabold tracking-tight text-white">
                        IT Support & <span className="text-transparent bg-clip-text bg-gradient-to-r from-indigo-400 to-sky-300">Consultancy Packages</span>
                    </h2>
                    <p className="mt-4 text-lg text-gray-400">
                        Solutions tailored to your business needs, from on-demand support to full strategic technology partnership.
                    </p>
                </div>

                {/* Packages Grid */}
                <div className="mt-16 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                    {plans.map((plan, index) => (
                        <motion.div
                            key={plan.name}
                            className={`relative flex flex-col rounded-2xl p-1 transition-all duration-300 group shadow-xl
                                ${plan.popular 
                                    ? 'bg-gradient-to-br from-indigo-600/50 to-sky-600/50 border-indigo-500/50' 
                                    : 'bg-slate-800/80 border border-slate-700/50 hover:border-sky-500/50'}
                            `}
                            initial={cardVariants.hidden}
                            whileInView={cardVariants.visible}
                            viewport={{ once: true, amount: 0.3 }}
                            transition={{ delay: index * 0.1 }}
                        >
                            <div className={`bg-slate-900/90 p-8 rounded-xl flex flex-col h-full`}>
                                
                                {/* Popular Tag */}
                                {plan.popular && (
                                    <div className="absolute top-0 left-1/2 -translate-x-1/2 -mt-4">
                                        <span className="inline-flex items-center px-4 py-1 rounded-full text-white font-semibold bg-gradient-to-r from-indigo-500 to-sky-400 shadow-lg shadow-indigo-500/30">
                                            <Sparkles className="w-4 h-4 mr-1" /> Most Strategic
                                        </span>
                                    </div>
                                )}

                                {/* Plan Header */}
                                <div className="flex-grow">
                                    <h3 className="text-3xl font-bold text-white mb-2">{plan.name}</h3>
                                    <p className="text-sm font-medium text-sky-400 mb-4">{plan.description}</p>
                                    
                                    {/* Pricing/Tier Label */}
                                    <div className="mt-4 mb-8 text-center bg-slate-800/70 p-4 rounded-xl border border-slate-700">
                                        <span className="text-2xl font-extrabold text-transparent bg-clip-text bg-gradient-to-r from-indigo-400 to-sky-300">
                                            {plan.priceLabel}
                                        </span>
                                        <p className="text-xs text-slate-500 mt-1">Tier of Service</p>
                                    </div>
                                    
                                    {/* Features List */}
                                    <ul className="mt-8 space-y-4">
                                        {plan.features.map((feature) => (
                                            <li key={feature} className="flex items-start">
                                                <CheckCircle2 className="w-5 h-5 text-sky-400 mr-3 flex-shrink-0 mt-0.5" /> 
                                                <span className="text-gray-300 text-sm">{feature}</span>
                                            </li>
                                        ))}
                                    </ul>
                                </div>
                                
                                {/* CTAs */}
                                <motion.a
                                    href={`https://wa.me/${WHATSAPP_NUMBER}?text=${encodeURIComponent(`I am interested in the ${plan.name} consultancy plan. Could we discuss the details?`)}`}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className={`mt-10 w-full flex items-center justify-center px-6 py-3 rounded-xl text-lg font-semibold shadow-lg transition-all 
                                        ${plan.popular 
                                            ? 'bg-gradient-to-r from-indigo-600 to-sky-500 text-white shadow-indigo-500/40' 
                                            : 'bg-slate-700 hover:bg-slate-600 text-white shadow-slate-900/40'
                                        }`}
                                    whileHover={{ scale: 1.02 }}
                                    whileTap={{ scale: 0.98 }}
                                >
                                    <WhatsAppLogo className="w-5 h-5 mr-2" />
                                    Inquire via WhatsApp
                                </motion.a>
                                <motion.a
                                    href={TEAMS_LINK}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="mt-3 w-full block text-center bg-transparent text-slate-400 hover:text-white px-6 py-3 rounded-xl text-sm font-medium shadow-lg flex items-center justify-center transition-colors"
                                    whileHover={{ scale: 1.02 }}
                                    whileTap={{ scale: 0.98 }}
                                >
                                    <TeamsLogo className="w-5 h-5 mr-2 opacity-70" />
                                    Join Teams Community
                                </motion.a>
                            </div>
                        </motion.div>
                    ))}
                </div>
            </div>
        </section>
    );
}